import React, { useState, useEffect, useRef, useCallback } from 'react';
import useGlobal from '../../../store/Store';
import { getResultLabels } from '../../../lib/labels';
import { Layout } from 'antd';
import { useParams, useHistory, withRouter } from 'react-router-dom';
import { getTestFile } from '../../../lib/api/aws-api';
import { compare, getMultiflowStatus } from '../../../lib/api/flow-api';
import { useInterval } from '../../../lib/useInterval';
import { isRecentAndProcessingInference } from '../../../lib/util/processing';
import { reformInferenceToPdfFile } from '../../../lib/util/reform';
import ResultSideBar from './ResultSideBar';
import Compare from './Compare';
import CompareHeader from './CompareHeader';
import { PdfContainer } from '../../../common/pdf/PdfContainer';
import { InferenceFile, PdfFile } from '../../../types';
import { MultiflowStatus } from '../../../types/flow';
import { processInferenceResult } from '../../../lib/util';
import { useGetAlgorithm } from '../../../lib/hooks';
import { DetailContainer } from '../../../components/layout/DetailContainer';
import { SplitOverview } from '../../../common/splitter/SplitOverview';
import { MatchOverview } from '../../../common/matcher/MatchOverview';

const { Content } = Layout;

const DemoResult = () => {
  let history = useHistory();

  const { inferenceId, algorithmId } = useParams() as {
    inferenceId: string;
    algorithmId: string;
  };

  const inferenceIds = useRef<string[]>([inferenceId]);
  const inferenceFile = useRef<null | InferenceFile>(null);

  const [pdfFile, setPdfFile] = useState<PdfFile>();
  const [inferenceList, setInferenceList] = useState<null | InferenceFile[]>(null);
  const [currentInferenceId, setCurrentInferenceId] = useState<string>(inferenceId);
  const [multiflowStatus, setMultiflowStatus] = useState<null | MultiflowStatus>(null);

  const [modalVisible, setModalVisible] = useState(false);
  const [globalState, globalActions] = useGlobal();

  const { algorithm } = useGetAlgorithm(algorithmId);

  useInterval(async () => {
    if (!inferenceList) return;
    try {
      updateInferenceListStatus(inferenceList);
    } catch (e) {}
  }, 2000);

  const reset = useCallback(() => {
    globalActions.setLabelOptions(null);
    globalActions.setLabelEdit(null);
    globalActions.removeLabels();
    inferenceFile.current = null;
  }, [globalActions, inferenceFile]);

  const loadLabels = useCallback(
    async inference => {
      const labels = getResultLabels(inference, inference.model, globalState.allLabels);
      globalActions.setLabels(labels);
    },
    [globalActions, globalState.allLabels]
  );

  const fetchInitialInferenceListStatus = useCallback(async () => {
    const results = await Promise.all(
      inferenceIds.current.map(async item => {
        return getTestFile(item);
      })
    );
    setInferenceList(results);
  }, [setInferenceList, inferenceIds]);

  const updateInferenceListStatus = useCallback(
    async (list: InferenceFile[]) => {
      const results = await Promise.all(
        inferenceIds.current.map(async item => {
          const inference = list && list.find(inference => inference.id === item);
          return !inference || isRecentAndProcessingInference(inference) ? await getTestFile(item) : inference;
        })
      );
      setInferenceList(results);
    },
    [inferenceIds, setInferenceList]
  );

  useEffect(() => {
    globalActions.setReadOnly(true);
    fetchInitialInferenceListStatus();
    return () => {
      reset();
      globalActions.setReadOnly(false);
    };
  }, [inferenceId, globalActions, reset, fetchInitialInferenceListStatus]);

  useEffect(() => {
    if (inferenceFile.current) {
      console.log(inferenceFile.current);
      loadLabels(inferenceFile.current);
    }
  }, [globalState.allLabels, loadLabels]);

  useEffect(() => {
    const setupInferenceFile = async () => {
      if (inferenceList && currentInferenceId) {
        const inferenceInList = inferenceList.find(inference => inference.id === currentInferenceId);

        // prevent loading loop
        if (!inferenceFile.current || inferenceFile.current.id !== currentInferenceId) {
          inferenceFile.current = inferenceInList || null;
          if (
            algorithm?.multiflow &&
            inferenceFile.current &&
            inferenceFile.current.run &&
            inferenceFile.current.run.id
          ) {
            const multiflowStatus: MultiflowStatus = await getMultiflowStatus(inferenceFile.current.run.id);
            setMultiflowStatus(multiflowStatus);
          }
          if (inferenceFile.current) {
            const reformedFile = processInferenceResult(inferenceFile.current);
            loadLabels(reformedFile);
            setPdfFile(reformInferenceToPdfFile(reformedFile));
          }
        }
      }
    };
    setupInferenceFile();
  }, [inferenceList, currentInferenceId, loadLabels, algorithm]);

  const goBack = () => {
    history.push(`/demo/${algorithmId}/models`);
  };

  const onInferenceSelect = (inferenceId: string) => {
    setCurrentInferenceId(inferenceId);
  };

  const onCompare = async (modelId: number, modelVersionId: string) => {
    if (inferenceFile.current && inferenceList) {
      const response = await compare(inferenceFile.current.id, modelId, modelVersionId);

      inferenceIds.current = inferenceIds.current.concat(response.id);
      updateInferenceListStatus(inferenceList);
    }
  };

  return (
    <>
      {algorithm && modalVisible && (
        <Compare
          visible={modalVisible}
          algorithm={algorithm}
          onCompare={onCompare}
          onClose={() => setModalVisible(false)}
        />
      )}
      {algorithm && (
        <>
          {algorithm.name === 'documentSplitter' ? (
            <>
              <SplitOverview inferenceFile={inferenceFile.current} goBack={goBack} />
            </>
          ) : algorithm.name === 'contentMatcher' ? (
            <>
              <MatchOverview inferenceFile={inferenceFile.current} goBack={goBack} />
            </>
          ) : (
            <>
              {inferenceFile.current && inferenceList && (
                <CompareHeader
                  currentInferenceId={inferenceFile.current.id}
                  inferenceList={inferenceList}
                  onInferenceSelect={onInferenceSelect}
                  onClose={goBack}
                  onCompare={() => setModalVisible(true)}
                />
              )}
              <DetailContainer>
                <Layout>
                  <Content>
                    <div className={'pdf-layout'} style={{ width: '100%' }}>
                      <PdfContainer
                        file={pdfFile}
                        labels={globalState.labels.filter(label => label && (label.exists || !label.reliable))}
                        showNonExisting={false}
                        onAnnotationChange={() => {}}
                      />
                    </div>
                  </Content>
                </Layout>
                <ResultSideBar multiflowStatus={multiflowStatus} inferenceFile={inferenceFile.current} />
              </DetailContainer>
            </>
          )}
        </>
      )}
    </>
  );
};

export default withRouter(DemoResult);
